import { SignOutButton } from "@clerk/clerk-react";
import { ChevronLeft, Clock, type LucideIcon } from "lucide-react";
import type { ReactNode } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { clerkEnabled } from "@/services/clerk";
import { useMe } from "@/features/members/hooks/useMe";
import { AppSidebar } from "@/components/AppSidebar";
import { GlobalSearch } from "@/components/GlobalSearch";
import { parentSection } from "@/lib/parentSection";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/ui/sidebar";

/** Full-page gate card — one per reason the members area stays closed. */
function Blocked({
  icon: Icon,
  title,
  description,
  cta,
}: {
  icon: LucideIcon;
  title: string;
  description: string;
  cta?: ReactNode;
}) {
  return (
    <div className="flex min-h-screen w-full items-center justify-center p-6">
      <div className="animate-page-enter bg-card ring-foreground/10 w-full max-w-md rounded-xl px-8 py-10 text-center ring-1">
        <div className="bg-muted text-muted-foreground mx-auto flex size-12 items-center justify-center rounded-full">
          <Icon className="size-6" strokeWidth={1.5} />
        </div>
        <h1 className="mt-5 text-xl font-medium tracking-tight">{title}</h1>
        <p className="text-muted-foreground mx-auto mt-2 max-w-sm text-sm leading-relaxed text-pretty">
          {description}
        </p>
        <div className="mt-7 flex flex-wrap items-center justify-center gap-2">
          {cta}
          {clerkEnabled && (
            <SignOutButton>
              <Button variant={cta ? "ghost" : "outline"}>Sign out</Button>
            </SignOutButton>
          )}
        </div>
      </div>
    </div>
  );
}

/** Members-area shell: inset sidebar + top bar + content. Session auth is
 * handled by ProtectedRoute; this layout adds the membership + role gates
 * once `me` loads (ports the source's (members)/layout.tsx). */
export default function MemberLayout() {
  const { data: me, isPending } = useMe(clerkEnabled);
  const parent = parentSection(useLocation().pathname);

  // No full-screen spinner while this loads. The shell is the same either
  // way — sidebar, nav, the page itself — and holding all of it behind an
  // auth handshake means a signed-out reader waits on Clerk to read a page
  // Clerk has nothing to do with. Only the footer, which is the one part that
  // differs, waits (see `sessionPending`).

  // Null for somebody signed out, and that is a state this shell renders
  // rather than refuses: the directory has a preview anybody may read, and a
  // stranger following a link from Soft Network should land in AFELL rather
  // than on a sign-in form. What they are shown of it is the server's
  // decision — every one of these pages asks it again.
  const member = me?.member ?? null;

  // A lapsed membership is deliberately *not* blocked: they renew themselves
  // from the billing page, and the server already serves them the preview
  // (`viewer_or_anonymous`). This shell used to show them a card saying to
  // contact an administrator, which is the one thing they no longer need to
  // do, and it sat in front of the billing page they were sent to.
  if (member && member.status === "pending_payment") {
    return (
      <Blocked
        icon={Clock}
        title="Membership not yet active"
        description="Your application was approved, but your membership hasn't been activated yet. Use the payment link from your approval email to finish up."
      />
    );
  }

  return (
    <SidebarProvider>
      <AppSidebar
        member={member}
        isMember={me?.isMember}
        hasFullAccess={me?.hasFullAccess}
        queues={me?.queues}
        sessionPending={isPending}
      />
      <SidebarInset>
        {/* Mobile only, and nothing but the sidebar trigger. The bar carried
            a title once, then a Preview badge and an Apply button; all three
            were noise above every page. On desktop it takes itself out of the
            layout and the page starts at the top. */}
        <header className="flex h-14 shrink-0 items-center gap-2 border-b px-4 md:hidden">
          {/* Mobile only: at that width the sidebar is a sheet that has to be
              opened, while on desktop it is simply always there. */}
          <SidebarTrigger className="-ml-1 md:hidden" />
          <Separator
            orientation="vertical"
            className="mr-2 data-vertical:h-4 data-vertical:self-auto md:hidden"
          />
          {/* The way back up, and the only thing this bar has ever carried
              besides the trigger. Without it a phone reader's one route out of
              a member's profile was the hamburger, above 56 pixels of empty
              rule. Only on the pages that have a parent: at the top level the
              sidebar is already the way around. */}
          {parent && (
            <Link
              to={parent.to}
              className="text-muted-foreground hover:text-foreground -ml-1 inline-flex items-center gap-1 text-sm"
            >
              <ChevronLeft className="size-4" />
              {parent.label}
            </Link>
          )}
          {/* No search box up here: the directory carries its own, above its
              results. The palette stays mounted for its ⌘K shortcut, which
              reaches threads, resources and help as well as members. Signing
              out lives in the identity menu at the foot of the sidebar. */}
          <GlobalSearch withTrigger={false} />
        </header>
        {/* No layout-level heading: every page renders its own, and having
            both meant the admin pages showed their title twice. */}
        <main className="flex-1 p-6">
          <Outlet />
        </main>
      </SidebarInset>
    </SidebarProvider>
  );
}
